import React, { useState } from "react";
import { motion as Motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Button from "../components/Button";
import trialsImg from "../assets/trials.webp";

import { FiChevronUp, FiChevronDown } from "react-icons/fi";

const Programs = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const programs = [
    {
      title: "Group Training",
      img: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      desc: "Collaborative drills focusing on game scenarios and team dynamics.",
    },
    {
      title: "Private Training",
      img: "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?q=80&w=735&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
      desc: "Personalized attention to master technical skills and individual tactics.",
    },
    {
      title: "Youth Development",
      img: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      desc: "Building the foundation for U8-U12 with joy and essential skills.",
    },
    {
      title: "Trial Session",
      img: trialsImg,
      desc: "Experience our elite training firsthand. Book a trial session to see your potential on the pitch.",
      link: "/trial-interest",
    },
  ];

  const faqs = [
    {
      q: "What age groups do you cater to?",
      a: "We provide training from aged 4 to 16, organized into specific age-appropriate squads (U4 to U16).",
    },
    {
      q: "What is the coach-to-student ratio?",
      a: "We maintain a low ratio of 1:10 for group sessions to ensure every player receives adequate attention.",
    },
    {
      q: "What equipment do I need to bring?",
      a: "Players should bring football boots (appropriate for grass), shin pads, and a water bottle. We provide balls and bibs.",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <div className="flex flex-col pt-16 md:pt-20 bg-[#f5f5f5]">
      <Helmet>
        <title>Reflection Futbol Academy | Football Training Programs</title>
        <meta
          name="description"
          content="Explore elite football training programs in Oklahoma City. RFA offers group training, private sessions, and youth development for ages 4-16 in OKC."
        />
        <link
          rel="canonical"
          href="https://reflectionfutbolacademy.com/programs"
        />

        {/* Social SEO (Open Graph) */}
        <meta
          property="og:title"
          content="Elite Football Programs | OKC | Reflection Futbol Academy"
        />
        <meta
          property="og:description"
          content="View our range of professional training programs in Oklahoma City. From beginners to elite athletes."
        />
      </Helmet>
      {/* 1. Page Hero */}
      <section className="relative py-12 md:py-20 bg-[#7D192A] flex items-center justify-center text-center overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1510279770292-4b34de9f5c23?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=60"
            srcSet="https://images.unsplash.com/photo-1510279770292-4b34de9f5c23?ixlib=rb-4.0.3&auto=format&fit=crop&w=640&q=60 640w,
                    https://images.unsplash.com/photo-1510279770292-4b34de9f5c23?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=60 1200w"
            sizes="100vw"
            alt="Professional football training at Reflection Futbol Academy"
            width={1200}
            height={800}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto site-gutter">
          <h1 className="page-hero-title text-white uppercase">Our Programs</h1>
          <div className="mt-3 md:mt-4 h-1 w-16 md:h-2 md:w-32 bg-[#F4B400] mx-auto rounded-full" />
        </div>
      </section>

      {/* 2. Program Cards Grid */}
      <section className="py-20 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto site-gutter">
          <Motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7"
          >
            {programs.map((prog, i) => (
              <Motion.div
                key={i}
                variants={itemVariants}
                className="surface-card rounded-2xl overflow-hidden shadow-soft flex flex-col group"
              >
                <div className="h-56 overflow-hidden relative">
                  <img
                    src={`${prog.img}${prog.img.includes("?") ? "&" : "?"}w=400&q=60&auto=format&fit=crop`}
                    alt={prog.title}
                    width={400}
                    height={224}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex-grow space-y-3.5">
                  <h2 className="card-title font-bold text-[#222222]">
                    {prog.title}
                  </h2>
                  <p className="body-text text-gray-600">{prog.desc}</p>
                </div>
                <div className="p-6 pt-0 flex justify-start">
                  <Button
                    to={prog.link || "/interest-form"}
                    variant="maroonOutline"
                    pill={true}
                    fullWidth={false}
                  >
                    LEARN MORE{" "}
                    <span className="sr-only">about {prog.title}</span>
                  </Button>
                </div>
              </Motion.div>
            ))}
          </Motion.div>
        </div>
      </section>

      {/* 3. FAQ Accordion Section */}
      <section className="py-20 bg-[#F5F5F5]">
        <div className="max-w-3xl mx-auto site-gutter">
          <h2 className="section-heading text-[#111111] uppercase text-center mb-12">
            Frequently Asked Questions
          </h2>
          <div className="mt-12 space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="surface-card rounded-xl overflow-hidden shadow-soft"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-inter font-bold text-[#222222] text-sm md:text-base lg:text-lg uppercase tracking-tight">
                    {faq.q}
                  </span>
                  {openFaq === i ? (
                    <FiChevronUp className="text-[#7D192A]" />
                  ) : (
                    <FiChevronDown className="text-gray-400" />
                  )}
                </button>
                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    openFaq === i ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="p-6 pt-0 body-text text-gray-600 bg-gray-50/50">
                    {faq.a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Programs;
